import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, Users, DollarSign, Clock, Tag, BarChart3 } from 'lucide-react';
import { logError } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 2 Analytics tab.
 * Owns its own data-fetching because the numbers are expensive to compute
 * and only needed when the tab is active.
 */
const AdminAnalyticsTab = ({ adminToken }) => {
  const [data, setData] = useState({
    retention: null,
    churn: null,
    aov: null,
    peakTimes: null,
    promoPerformance: null,
    revenueByPlan: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadAnalytics = useCallback(async () => {
    if (!adminToken) return;
    setIsLoading(true);
    const headers = { Authorization: `Bearer ${adminToken}` };
    const endpoints = [
      ['retention', 'retention'],
      ['churn', 'churn'],
      ['aov', 'aov'],
      ['peakTimes', 'peak-times'],
      ['promoPerformance', 'promo-performance'],
      ['revenueByPlan', 'revenue-by-plan'],
    ];
    try {
      const results = await Promise.all(
        endpoints.map(([, path]) =>
          fetch(`${API_URL}/api/admin/analytics/${path}`, { headers }).then((r) =>
            r.ok ? r.json() : null
          )
        )
      );
      const next = {};
      endpoints.forEach(([key], i) => {
        next[key] = results[i];
      });
      setData(next);
    } catch (error) {
      logError('Failed to load Phase 2 analytics:', error);
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const { retention, churn, aov, peakTimes, promoPerformance, revenueByPlan } = data;

  const maxHour = peakTimes?.by_hour?.reduce((m, h) => Math.max(m, h.bookings), 0) || 1;
  const maxDay = peakTimes?.by_day?.reduce((m, d) => Math.max(m, d.bookings), 0) || 1;

  return (
    <div data-testid="admin-analytics-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Phase 2 Analytics</h2>
        <button
          onClick={loadAnalytics}
          data-testid="refresh-analytics-btn"
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #10b981',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: '#10b981',
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          Refresh
        </button>
      </div>

      {isLoading && (
        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Loading analytics…</p>
      )}

      <div style={{ display: 'grid', gap: '1.5rem' }}>
        {/* ===== Retention ===== */}
        <Card data-testid="retention-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={18} color="#10b981" /> Customer Retention
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!retention ? (
              <p style={{ color: '#6b7280' }}>No data yet.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                <StatBlock label="Total Customers" value={retention.total_customers} />
                <StatBlock label="Rebooked in 30d" value={`${retention.retention_rate_30d}%`} sub={`${retention.retained_30_days} customers`} />
                <StatBlock label="Rebooked in 60d" value={`${retention.retention_rate_60d}%`} sub={`${retention.retained_60_days} customers`} />
                <StatBlock label="Rebooked in 90d" value={`${retention.retention_rate_90d}%`} sub={`${retention.retained_90_days} customers`} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* ===== Churn ===== */}
        <Card data-testid="churn-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={18} color="#ef4444" /> Subscriber Churn
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!churn ? (
              <p style={{ color: '#6b7280' }}>No data yet.</p>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                  <StatBlock label="Tracked Subscribers" value={churn.total_tracked_subscribers} />
                  <StatBlock label="Churn Rate" value={`${churn.churn_rate_pct}%`} sub={`${churn.churned_subscribers} churned`} color="#ef4444" />
                  <StatBlock label="Retention Rate" value={`${churn.retention_rate_pct}%`} color="#10b981" />
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.75rem' }}>
                  Churned = no booking in the last 45 days. Actual Stripe subscription state will refine this.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* ===== AOV by plan ===== */}
        <Card data-testid="aov-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <DollarSign size={18} color="#f59e0b" /> Average Order Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!aov ? (
              <p style={{ color: '#6b7280' }}>No data yet.</p>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <StatBlock label="Overall AOV" value={`$${(aov.overall_aov || 0).toFixed(2)}`} sub={`Across ${aov.total_bookings} bookings`} />
                </div>
                {aov.by_plan?.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem 0' }}>Plan</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Bookings</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Revenue</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>AOV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aov.by_plan.map((row) => (
                        <tr key={row.plan_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.5rem 0', fontWeight: 600 }}>{row.plan_name}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>{row.bookings}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>${row.total_revenue.toFixed(2)}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right', color: '#f59e0b', fontWeight: 600 }}>
                            ${row.aov.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ color: '#6b7280' }}>No bookings yet.</p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ===== Revenue by plan ===== */}
        <Card data-testid="revenue-by-plan-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BarChart3 size={18} color="#3b82f6" /> Revenue by Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!revenueByPlan || !revenueByPlan.by_plan?.length ? (
              <p style={{ color: '#6b7280' }}>No revenue data yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {revenueByPlan.by_plan.map((row) => (
                  <div key={row.plan_id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 600 }}>{row.plan_name}</span>
                      <span>${row.total_revenue.toFixed(2)} • {row.share_pct}%</span>
                    </div>
                    <div style={{ height: '8px', backgroundColor: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${row.share_pct}%`, backgroundColor: '#3b82f6' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ===== Peak times ===== */}
        <Card data-testid="peak-times-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={18} color="#8b5cf6" /> Peak Booking Times
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!peakTimes ? (
              <p style={{ color: '#6b7280' }}>No data yet.</p>
            ) : (
              <>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Peak day: <strong>{peakTimes.peak_day?.day}</strong> · Peak hour: <strong>{peakTimes.peak_hour?.hour}:00</strong>
                </p>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>By Day of Week</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.25rem', marginBottom: '1rem' }}>
                  {peakTimes.by_day?.map((d) => (
                    <div key={d.day} style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          height: '60px',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          backgroundColor: '#f3f4f6',
                          borderRadius: '4px',
                        }}
                      >
                        <div
                          style={{
                            width: '60%',
                            height: `${(d.bookings / maxDay) * 100}%`,
                            backgroundColor: '#8b5cf6',
                            borderRadius: '4px 4px 0 0',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', color: '#6b7280' }}>{d.day.slice(0, 3)}</div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600 }}>{d.bookings}</div>
                    </div>
                  ))}
                </div>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>By Hour of Day</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: '2px' }}>
                  {peakTimes.by_hour?.map((h) => (
                    <div key={h.hour} title={`${h.hour}:00 — ${h.bookings} bookings`} style={{ textAlign: 'center' }}>
                      <div style={{ height: '40px', display: 'flex', alignItems: 'flex-end', backgroundColor: '#f3f4f6' }}>
                        <div
                          style={{
                            width: '100%',
                            height: `${(h.bookings / maxHour) * 100}%`,
                            backgroundColor: '#8b5cf6',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: '0.6rem', color: '#6b7280' }}>{h.hour}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ===== Promo performance ===== */}
        <Card data-testid="promo-performance-card">
          <CardHeader>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Tag size={18} color="#ec4899" /> Promo Code Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!promoPerformance ? (
              <p style={{ color: '#6b7280' }}>No data yet.</p>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <StatBlock label="Total Bookings" value={promoPerformance.total_bookings} />
                  <StatBlock label="Promo Bookings" value={promoPerformance.promo_bookings} />
                  <StatBlock label="Total Discount Given" value={`$${(promoPerformance.total_discount_given || 0).toFixed(2)}`} color="#ec4899" />
                </div>
                {promoPerformance.by_code?.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem 0' }}>Code</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Bookings</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Revenue</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Discount</th>
                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>% Bookings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoPerformance.by_code.map((row) => (
                        <tr key={row.code} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.5rem 0', fontWeight: 600, color: '#ec4899' }}>{row.code}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>{row.bookings}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>${row.revenue.toFixed(2)}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right', color: '#6b7280' }}>-${row.discount_given.toFixed(2)}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>{row.share_of_bookings_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No promo codes used yet.</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatBlock = ({ label, value, sub, color }) => (
  <div style={{ padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{label}</div>
    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: color || '#1f2937' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{sub}</div>}
  </div>
);

export default AdminAnalyticsTab;
